import React, { useState } from 'react';
import { useFormContext } from '@grnhse/seedling/lib/birch/components/forms/form';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { TertiaryButton } from '@grnhse/seedling/lib/birch/components/buttons';
import { Label } from '@grnhse/seedling/lib/birch/typography';
import { HelpTooltip } from '@grnhse/seedling/lib/birch/components/tooltips';
import KeywordForm from 'talent_search/components/keyword_form';
import { withLocalePrefix } from 'shared/utils/translation';
import RefreshIcon from '@grnhse/seedling/lib/birch/icons/refresh';
import { useCalibrationModal } from 'talent_matching/contexts/calibration_modal_context';
import { useCalibrationSettings } from 'talent_matching/api';
import { Paths as TmPaths } from 'talent_matching/types';

const t = withLocalePrefix('talent_matching.rediscovery.filter_row.keywords');

type FilterType = 'can_have' | 'must_have' | 'does_not_have';

const RelevanceKeywordsFilters = () => {
  const { watch, setValue } = useFormContext();
  const { paths } = useCalibrationModal();
  const [canHave, mustHave, doesNotHave] = watch([
    'can_have',
    'must_have',
    'does_not_have',
  ]);

  const [showAdditionalKeywordsSection, setShowAdditionalKeywordsSection] =
    useState(mustHave.length > 0 || doesNotHave.length > 0);
  const showKeywordsButton =
    !showAdditionalKeywordsSection || !(mustHave.length || doesNotHave.length);

  const { data: calibrationData } = useCalibrationSettings(
    paths as unknown as TmPaths
  );

  const handleAdd = (type: FilterType, keywords: string[]) => {
    setValue(type, keywords, { shouldDirty: true });
  };

  const handleRemove = (type: FilterType, keyword: string) => {
    setValue(
      type,
      (watch(type) as string[]).filter((k: string) => k !== keyword),
      { shouldDirty: true }
    );
  };

  const applyCalibrationSkillKeywords = () => {
    const skills = calibrationData?.settings?.skills ?? [];
    setValue(
      'can_have',
      skills.map((s) => s.name),
      { shouldDirty: true }
    );
  };

  return (
    <Stack spacing={2}>
      <Stack spacing={1} alignItems="flex-start">
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Label>{t('can_have')}</Label>
          <HelpTooltip
            label={t('can_have_description')}
            ariaLabel={t('can_have_description')}
          />
        </Stack>
        <KeywordForm
          filterType="can_have"
          values={canHave}
          onAdd={(keywords) => handleAdd('can_have', keywords)}
          onRemove={(keyword) => handleRemove('can_have', keyword)}
          booleanIndicator={t('or')}
          pageName="rediscovery"
        />
        <TertiaryButton
          onClick={applyCalibrationSkillKeywords}
          size="md"
          icon={RefreshIcon}
          placement="left"
          disabled={!calibrationData?.settings?.skills?.length}
        >
          {t('reload_keywords_from_calibration')}
        </TertiaryButton>
      </Stack>

      {showAdditionalKeywordsSection && (
        <Stack spacing={2}>
          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Label>{t('must_have')}</Label>
              <HelpTooltip
                label={t('must_have_description')}
                ariaLabel={t('must_have_description')}
              />
            </Stack>
            <KeywordForm
              filterType="must_have"
              values={mustHave}
              onAdd={(keywords) => handleAdd('must_have', keywords)}
              onRemove={(keyword) => handleRemove('must_have', keyword)}
              booleanIndicator={t('and')}
              pageName="rediscovery"
            />
          </Stack>

          <Stack spacing={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Label>{t('does_not_have')}</Label>
              <HelpTooltip
                label={t('does_not_have_description')}
                ariaLabel={t('does_not_have_description')}
              />
            </Stack>
            <KeywordForm
              filterType="does_not_have"
              values={doesNotHave}
              onAdd={(keywords) => handleAdd('does_not_have', keywords)}
              onRemove={(keyword) => handleRemove('does_not_have', keyword)}
              booleanIndicator={t('and')}
              pageName="rediscovery"
            />
          </Stack>
        </Stack>
      )}

      {showKeywordsButton && (
        <Stack alignItems="flex-start">
          <TertiaryButton
            onClick={() =>
              setShowAdditionalKeywordsSection(!showAdditionalKeywordsSection)
            }
            size="md"
          >
            {showAdditionalKeywordsSection
              ? t('hide_more_keywords')
              : t('show_more_keywords')}
          </TertiaryButton>
        </Stack>
      )}
    </Stack>
  );
};

export default RelevanceKeywordsFilters;
